//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import {
  EmailSignupCountResponse,
  EmailSignupEnabledResponse,
  LabelResponse,
  UnifiedMetricsBenchmarksResponse,
  UnifiedMetricsBubbleStatsResponse,
  UnifiedMetricsGraphStatsResponse,
  UnifiedMetricsItemProductStatsResponse,
  UnifiedMetricsLiftStatsResponse,
  UnifiedMetricsTouchpointStatsResponse,
} from '@bvengineering/metrics-gateway-service-types';

import {
  CampaignAnalyticsStatsResponse,
  ICampaignAnalyticsStatsRequest,
  IContentAcquisitionResponse,
  IItemProductStatsRequest,
  IStatsExportRequest,
  IStatsRequest,
  SamplingCampaignReviewStatsRequest,
  UnifiedMetricsMetadataResponseWithCurrencyOverride,
  UnifiedMetricsReviewStatsResponse,
} from '@apis/metrics/metricsApiModel';
import { makeParams } from '@utils/makeParams';
import { serializedGatewayFetch } from '@utils/sessionFetch';
import { ISerializedResponse } from '@utils/types/SerializedResponse';

function metricsGatewayFetch(
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> {
  return serializedGatewayFetch(
    'metrics_gateway',
    `/api/v1/unified-metrics${endpoint}`,
    init,
  );
}

function emailSignupsGatewayFetch(
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> {
  return serializedGatewayFetch(
    'metrics_gateway',
    `/api/v2/email-signup${endpoint}`,
    init,
  );
}

function webGatewayMediaFetch(
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> {
  return serializedGatewayFetch(
    'web_gateway',
    `/api/v1/media${endpoint}`,
    init,
  );
}

/**
 *
 * Gets metrics metadata for the given client
 *
 * /api/v1/unified-metrics/metadata
 *
 * @returns UnifiedMetricsMetadataResponseWithCurrencyOverride[]
 */
export async function getMetadata(): Promise<UnifiedMetricsMetadataResponseWithCurrencyOverride> {
  const res = await metricsGatewayFetch('/metadata');
  return await res.json<UnifiedMetricsMetadataResponseWithCurrencyOverride>();
}

/**
 *
 * Gets bubble stats. Returns a single element array of bubble stats
 *
 * /api/v1/unified-metrics/bubble-stats
 *
 * @returns UnifiedMetricsBubbleStatsResponse[]
 */
export async function getBubbleStats(
  req: IStatsRequest,
  init?: RequestInit,
): Promise<UnifiedMetricsBubbleStatsResponse[]> {
  const queryParams = makeParams(req);
  const res = await metricsGatewayFetch(`/bubble-stats?${queryParams}`, init);
  return await res.json<UnifiedMetricsBubbleStatsResponse[]>();
}

/**
 *
 * Gets email signup count
 *
 * /api/v1/emailSignups/{touchpoint}/email-count
 *
 * @returns EmailSignupCountResponse
 */
export async function getEmailSignupCount(
  req: IStatsRequest,
): Promise<EmailSignupCountResponse> {
  const queryParams = makeParams(req);
  const res = await emailSignupsGatewayFetch(`/email-count?${queryParams}`);
  return await res.json<EmailSignupCountResponse>();
}

/**
 *
 * Gets email signup count
 *
 * /api/v1/emailSignups/{touchpoint}/email-count
 *
 * @returns EmailSignupCountResponse
 */
export async function getEmailSignupEnabled(
  req: IStatsRequest,
): Promise<boolean> {
  const queryParams = makeParams(req);
  const res = await emailSignupsGatewayFetch(`/enabled?${queryParams}`);
  return (await res.json<EmailSignupEnabledResponse>()).enabled;
}

/**
 *
 * Get touchpoint stats for a client for a given date range
 *
 * /api/v1/unified-metrics/touchpoint-stats
 *
 * @returns UnifiedMetricsTouchpointStatsResponse[]
 */
export async function getTouchpointStats(
  req: IStatsRequest,
): Promise<UnifiedMetricsTouchpointStatsResponse[]> {
  const queryParams = makeParams(req);
  const res = await metricsGatewayFetch(`/touchpoint-stats?${queryParams}`);
  return await res.json<UnifiedMetricsTouchpointStatsResponse[]>();
}

/**
 *
 * Get graph stats for a client for a given date range
 *
 * /api/v1/unified-metrics/graph-stats
 *
 * @returns UnifiedMetricsGraphStatsResponse[]
 */
export async function getGraphStats(
  req: IStatsRequest | ICampaignAnalyticsStatsRequest,
  init?: RequestInit,
): Promise<UnifiedMetricsGraphStatsResponse[]> {
  const queryParams = makeParams(req);
  const res = await metricsGatewayFetch(`/graph-stats?${queryParams}`, init);
  return await res.json<UnifiedMetricsGraphStatsResponse[]>();
}

/**
 *
 * Get lift stats for a client for a given date range
 *
 * /api/v1/unified-metrics/lift-stats
 *
 * @returns UnifiedMetricsLiftStatsResponse
 */
export async function getLiftStats(
  req: IStatsRequest,
): Promise<UnifiedMetricsLiftStatsResponse> {
  const queryParams = makeParams(req);
  const res = await metricsGatewayFetch(`/lift-stats?${queryParams}`);
  return await res.json<UnifiedMetricsLiftStatsResponse>();
}

/**
 *
 * Get item stats for a client for a given date range
 *
 * /api/v1/unified-metrics/item-stats
 *
 * @returns UnifiedMetricsItemProductStatsResponse[]
 */
export async function getItemStats(
  req: IItemProductStatsRequest,
): Promise<UnifiedMetricsItemProductStatsResponse[]> {
  const queryParams = makeParams(req);
  const res = await metricsGatewayFetch(`/item-stats?${queryParams}`);
  return await res.json<UnifiedMetricsItemProductStatsResponse[]>();
}

/**
 *
 * Get review revenue stats for a client
 *
 * /api/v1/unified-metrics/review-stats
 *
 */
export async function getReviewStats(
  req: SamplingCampaignReviewStatsRequest,
  init?: RequestInit,
): Promise<UnifiedMetricsReviewStatsResponse> {
  const queryParams = makeParams(req);
  const res = await metricsGatewayFetch(`/review-stats?${queryParams}`, init);
  return res.json<UnifiedMetricsReviewStatsResponse>();
}

/**
 *
 * Get item stats for a client for a given date range
 *
 * /api/v1/unified-metrics/tagged-product-stats
 *
 * @returns UnifiedMetricsItemProductStatsResponse[]
 */
export async function getTaggedProductStats(
  req: IItemProductStatsRequest,
): Promise<UnifiedMetricsItemProductStatsResponse[]> {
  const queryParams = makeParams(req);
  const res = await metricsGatewayFetch(`/tagged-product-stats?${queryParams}`);
  return await res.json<UnifiedMetricsItemProductStatsResponse[]>();
}

/**
 *
 * Get product stats for a client for a given date range
 *
 * /api/v1/unified-metrics/product-stats
 *
 * @returns UnifiedMetricsItemProductStatsResponse[]
 */
export async function getProductStats(
  req: IItemProductStatsRequest,
): Promise<UnifiedMetricsItemProductStatsResponse[]> {
  const queryParams = makeParams(req);
  const res = await metricsGatewayFetch(`/product-stats?${queryParams}`);
  return await res.json<UnifiedMetricsItemProductStatsResponse[]>();
}

/**
 *
 * Get contributor stats for a client for a given date range
 *
 * /api/v1/unified-metrics/contributor-stats
 *
 * @returns UnifiedMetricsItemProductStatsResponse[]
 */
export async function getContributorStats(
  req: IItemProductStatsRequest,
): Promise<UnifiedMetricsItemProductStatsResponse[]> {
  const queryParams = makeParams(req);
  const res = await metricsGatewayFetch(`/contributor-stats?${queryParams}`);
  return await res.json<UnifiedMetricsItemProductStatsResponse[]>();
}

/**
 *
 * Get source stats for a client for a given date range
 *
 * /api/v1/unified-metrics/source-stats
 *
 * @returns UnifiedMetricsItemProductStatsResponse[]
 */
export async function getSourceStats(
  req: IItemProductStatsRequest,
): Promise<UnifiedMetricsItemProductStatsResponse[]> {
  const queryParams = makeParams(req);
  const res = await metricsGatewayFetch(`/source-stats?${queryParams}`);
  return await res.json<UnifiedMetricsItemProductStatsResponse[]>();
}

/**
 *
 * Get item-source stats for a client for a given date range
 *
 * /api/v1/unified-metrics/item-source-stats
 *
 * @returns UnifiedMetricsItemProductStatsResponse[]
 */
export async function getItemSourceStats(
  req: IItemProductStatsRequest,
): Promise<UnifiedMetricsItemProductStatsResponse[]> {
  const queryParams = makeParams(req);
  const res = await metricsGatewayFetch(`/item-source-stats?${queryParams}`);
  return await res.json<UnifiedMetricsItemProductStatsResponse[]>();
}

/**
 *
 * Get benchmark stats for use in benchmark graphs
 *
 * /api/v1/unified-metrics/benchmarks
 *
 * @returns UnifiedMetricsBenchmarksResponse
 */
export async function getBenchmarkStats(
  req: IStatsRequest,
): Promise<UnifiedMetricsBenchmarksResponse> {
  const queryParams = makeParams(req);
  const res = await metricsGatewayFetch(`/benchmarks?${queryParams}`);
  return await res.json<UnifiedMetricsBenchmarksResponse>();
}

/**
 *
 * Get campaign stats
 *
 * /api/v1/unified-metrics/campaign-stats
 *
 * @returns CampaignAnalyticsStatsResponse
 */
export async function getCampaignAnalyticsStats(
  req: ICampaignAnalyticsStatsRequest,
  init?: RequestInit,
): Promise<CampaignAnalyticsStatsResponse[]> {
  const queryParams = makeParams(req);
  const res = await metricsGatewayFetch(`/campaign-stats?${queryParams}`, init);
  return res.json<CampaignAnalyticsStatsResponse[]>();
}

/**
 *
 * Get content acquisition stats for use in content acquisition and distribution module
 *
 * /api/v1/media/stats
 *
 * @returns IContentAcquisitionResponse
 */
export async function getContentAcquisitionStats(
  req: IStatsRequest,
): Promise<IContentAcquisitionResponse> {
  const queryParams = makeParams(req);
  const res = await webGatewayMediaFetch(`/stats?${queryParams}`);
  return await res.json<IContentAcquisitionResponse>();
}

/**
 * Get a list of labels with their count
 *
 * /api/v1/unified-metrics/labels
 *
 * @returns LabelsResponse[]
 */
export async function getLabels(req: IStatsRequest): Promise<LabelResponse[]> {
  const queryParams = makeParams(req);
  const res = await metricsGatewayFetch(`/labels?${queryParams}`);
  return await res.json<LabelResponse[]>();
}

export async function getExportZip(
  req: IStatsExportRequest,
): Promise<BlobPart> {
  const queryParams = makeParams(req);
  const res = await metricsGatewayFetch(`/export.zip?${queryParams}`);
  return await res.blob();
}

export async function getExportToEmail(
  req: IStatsExportRequest,
): Promise<void> {
  const queryParams = makeParams(req);
  metricsGatewayFetch(`/export?${queryParams}`, { method: 'POST' });
}

export async function getExportById(exportId: string): Promise<BlobPart> {
  const res = await metricsGatewayFetch(`/export/${exportId}`);
  return res.blob();
}

export async function getEmailsCsv(req: IStatsRequest): Promise<BlobPart> {
  const queryParams = makeParams(req);
  const res = await emailSignupsGatewayFetch(`/emails.csv?${queryParams}`);
  return await res.blob();
}
